import type { BadgeColor } from '@odin-labs/components';
import moment from 'moment-timezone';
import { effectiveRevision, type SiteLayoutState } from 'siteLayout';

/** The designer route's `version` segment: the draft, the effective revision, or a revision by id. */
export const DRAFT_VERSION = 'draft';
export const CURRENT_VERSION = 'current';

export type DesignerTab = 'zones' | 'entries';

export const siteLayoutDesignerPath = (
  jobsiteId: string,
  version: string,
  tab?: DesignerTab,
): string =>
  `/jobsite/${jobsiteId}/settings/site-layout/${version}${
    tab ? `/${tab}` : ''
  }`;

export const siteLayoutTabPath = (jobsiteId: string): string =>
  `/jobsite/${jobsiteId}/settings/site-layout`;

export type LayoutVersionStatus = 'DRAFT' | 'CURRENT' | 'PREVIOUS';

export type LayoutVersionRow = {
  /** The designer route's version segment for this row. */
  id: string;
  name: string;
  status: LayoutVersionStatus;
  /** When it was published, or an empty string for the draft. */
  publishedAt: string;
  /** Who published it, or an empty string for the draft. */
  publishedBy: string;
};

/** Older versions carry no badge: their dates say when they applied. */
export const versionStatusBadge: Record<
  LayoutVersionStatus,
  { text: string; color: BadgeColor } | null
> = {
  DRAFT: { text: 'Unpublished', color: 'purple' },
  CURRENT: { text: 'Published', color: 'green' },
  PREVIOUS: null,
};

export const formatPublishedAt = (iso: string | null): string =>
  iso ? moment(iso).format('MMM D, YYYY h:mm A') : '';

/** The site's layout versions newest first: the draft, then every revision. */
export const toLayoutVersionRows = (
  state: SiteLayoutState,
): LayoutVersionRow[] => {
  const effective = effectiveRevision(state.revisions);
  const rows: LayoutVersionRow[] = [];
  if (state.draft) {
    rows.push({
      id: DRAFT_VERSION,
      name: `Version ${state.draft.number}`,
      status: 'DRAFT',
      publishedAt: '',
      publishedBy: '',
    });
  }
  [...state.revisions].reverse().forEach((revision) => {
    rows.push({
      id: revision.id,
      name: `Version ${revision.number}`,
      status: revision.id === effective?.id ? 'CURRENT' : 'PREVIOUS',
      publishedAt: formatPublishedAt(revision.publishedAt),
      publishedBy: revision.publishedBy ?? '',
    });
  });
  return rows;
};
